/**
 * generateAlerts — pure function that derives smart alerts from the user's
 * real financial data. Replaces the static `alerts` array from mockData.js.
 *
 * Returns: Alert[] matching the shape used by Overview.js
 *   { id, type: 'danger'|'warning'|'info'|'success', title, desc, action, tab }
 */
export function generateAlerts({ budgetData, netWorthData, retirementData, rsusData }) {
  const alerts = [];
  let id = 1;

  const fmt = (n) => '$' + Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 });

  const bud    = budgetData    || {};
  const cats   = bud.categories   || [];
  const nw     = netWorthData  || {};
  const nwAssets = nw.assets || [];
  const retire = retirementData || { successRate: 0 };

  // ── Over-budget categories ────────────────────────────────────────────────
  if (bud.income > 0 && cats.length > 0) {
    cats
      .filter(c => c.budgeted > 0 && c.spent > c.budgeted)
      .forEach(cat => {
        const over = cat.spent - cat.budgeted;
        const pct  = Math.round((over / cat.budgeted) * 100);
        alerts.push({
          id:     id++,
          type:   pct > 20 ? 'danger' : 'warning',
          title:  `${cat.name} ${pct}% over budget`,
          desc:   `Spent ${fmt(cat.spent)} vs ${fmt(cat.budgeted)} budget — ${fmt(over)} over this month`,
          action: 'View Budget →',
          tab:    'budget',
        });
      });
  }

  // ── Low emergency fund ────────────────────────────────────────────────────
  if (bud.income > 0 && cats.length > 0) {
    const essentialNames = ['Housing', 'Food & Dining', 'Transportation', 'Utilities', 'Healthcare', 'Groceries', 'Rent', 'Mortgage'];
    const monthlyEssential = cats
      .filter(c => essentialNames.some(n => c.name.includes(n)))
      .reduce((s, c) => s + c.budgeted, 0)
      || cats.reduce((s, c) => s + c.budgeted, 0) * 0.5;  // fallback: 50% of total budget

    const sixMonths = monthlyEssential * 6;
    const cashAssets = nwAssets
      .filter(a => a.type === 'Cash')
      .reduce((s, a) => s + a.value, 0);

    if (sixMonths > 0 && cashAssets < sixMonths * 0.75) {
      const months = cashAssets > 0 ? (cashAssets / monthlyEssential).toFixed(1) : 0;
      alerts.push({
        id:     id++,
        type:   cashAssets < sixMonths * 0.25 ? 'danger' : 'warning',
        title:  `Emergency fund: ${months} months of expenses`,
        desc:   `You have ${fmt(cashAssets)} in cash. Aim for ${fmt(sixMonths)} (6 months of essential expenses)`,
        action: 'View Goals →',
        tab:    'goals',
      });
    }
  }

  // ── Upcoming RSU vest ─────────────────────────────────────────────────────
  if (rsusData?.vestingSchedule) {
    const today = new Date();
    const upcoming = rsusData.vestingSchedule.find(v => {
      if (v.vested) return false;
      const days = Math.round((new Date(v.date) - today) / 86400000);
      return days >= 0 && days <= 30;
    });
    if (upcoming) {
      const days  = Math.round((new Date(upcoming.date) - today) / 86400000);
      const value = upcoming.shares * rsusData.currentPrice;
      const tax   = value * (rsusData.taxRate || 0.37);
      const dateStr = new Date(upcoming.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      alerts.push({
        id:     id++,
        type:   'warning',
        title:  `RSU Vest in ${days} day${days !== 1 ? 's' : ''}`,
        desc:   `Set aside ~${fmt(tax)} for taxes on ${upcoming.shares} shares vesting ${dateStr}`,
        action: 'View RSUs →',
        tab:    'rsus',
      });
    }
  }

  // ── Retirement success rate ───────────────────────────────────────────────
  if (retire.successRate > 0) {
    const sr = retire.successRate;
    alerts.push({
      id:     id++,
      type:   sr >= 85 ? 'success' : sr >= 70 ? 'info' : 'danger',
      title:  `Monte Carlo: ${sr}% success rate`,
      desc:   sr >= 85
        ? 'Your retirement plan is on track. Target is 85%+.'
        : `Your plan succeeds in ${sr}% of simulations. Target is 85%+.`,
      action: 'Open Planner →',
      tab:    'retirement',
    });
  }

  // ── Missing estate documents ──────────────────────────────────────────────
  const missing = (nw.estatePlanning || []).filter(e => e.status === 'missing');
  if (missing.length > 0) {
    alerts.push({
      id:     id++,
      type:   'danger',
      title:  `${missing.length} estate document${missing.length > 1 ? 's' : ''} missing`,
      desc:   `Missing: ${missing.map(e => e.item).join(', ')}`,
      action: 'View Net Worth →',
      tab:    'networth',
    });
  }

  return alerts;
}
